import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FieldMetaState, useField } from 'react-final-form'
import { Option } from '@cargomart/ui'
import { FieldValidator } from 'final-form'
import { checkFieldError } from 'helpers/form'
import { handleFetchResponse } from 'src/api/errors'
import { useGetAddressMutation } from 'src/modules/customer/api'
import { SuggestionAddressItem } from 'src/schema'

import { BaseAsyncSelect } from '../components/Select/BaseAsyncSelect'
import { IBaseAsyncSelect } from '../components/Select/types'

interface IProps
  extends Pick<
    IBaseAsyncSelect<SuggestionAddressItem>,
    'placeholder' | 'size' | 'isClearable' | 'isDisabled' | 'className' | 'maxOptionsToDisplay' | 'maxOptionListHeight'
  > {
  name: string
  calculateError?: (meta: FieldMetaState<SuggestionAddressItem>) => string | boolean | undefined
  className?: string
  handleErrorSearch?: () => void
  handleSearch?: (val: string) => void
  hasSearchIcon?: boolean
  mobileName?: string
  onPickOption?: (option?: SuggestionAddressItem) => void
  validate?: FieldValidator<SuggestionAddressItem>
}

const getOptionName = (option?: SuggestionAddressItem) => option?.value || ''

export const MIN_QUERY_LENGTH = 3

export const FieldAddressSelect = memo(function FieldAddressSelect({
  className,
  name,
  placeholder = 'Название или ИНН',
  mobileName = placeholder,
  size = 'nm',
  isClearable = true,
  hasSearchIcon = true,
  onPickOption,
  calculateError = checkFieldError,
  validate,
  handleErrorSearch,
  handleSearch,
  ...props
}: IProps) {
  const { input, meta } = useField<SuggestionAddressItem>(name, { validate })
  const { value: selectedOption, onChange } = input
  const defaultValue = useRef(selectedOption)

  const [loadOptions, { data, isLoading }] = useGetAddressMutation()
  const lastSearch = useRef('')

  const [formatedData, setFormatedData] = useState<Array<SuggestionAddressItem>>([])
  useEffect(() => {
    setFormatedData(data?.map((item) => ({ ...item, id: item.value })) || [])
  }, [data])

  const handleRequestOptions = useCallback(
    (searchInfo: { search: string }) => {
      const newSearch = searchInfo.search || selectedOption?.value || ''
      if (lastSearch.current === newSearch) {
        return
      }
      lastSearch.current = newSearch

      let res = Promise.reject()
      if (newSearch.trim().length >= MIN_QUERY_LENGTH) {
        res = loadOptions(newSearch) as unknown as Promise<never>
        res.then(
          handleFetchResponse(
            (res) => {
              if (res === null) {
                handleErrorSearch && handleErrorSearch()

                return
              }
              handleSearch && handleSearch(newSearch)
            },
            () => handleErrorSearch && handleErrorSearch()
          )
        )
      } else {
        setFormatedData([])
      }

      return res as Promise<void>
    },
    [selectedOption, loadOptions]
  )

  const handleChange = useCallback(
    (option?: SuggestionAddressItem) => {
      onChange(option)
      onPickOption?.(option)
    },
    [onChange, onPickOption]
  )

  const error = useMemo(() => calculateError(meta), [meta, calculateError])

  return (
    <BaseAsyncSelect<SuggestionAddressItem>
      {...props}
      autoFocus
      className={className}
      debounceTime={300}
      defaultOption={defaultValue.current}
      error={error}
      fullscreenName={mobileName}
      isClearable={isClearable}
      isLoading={isLoading}
      noOptionsMessageWithEmptySearch="Начните вводить для поиска требуется 3 символа"
      optionsLength={formatedData?.length}
      placeholder={selectedOption ? getOptionName(selectedOption) : placeholder}
      prefix={undefined}
      size={size}
      validateForEmpty={(value) => value?.length > MIN_QUERY_LENGTH}
      value={getOptionName(selectedOption) as unknown as SuggestionAddressItem}
      onChange={handleChange}
      // @ts-ignore
      onRequestOptions={handleRequestOptions}
    >
      {formatedData?.map((option) => <Option key={option.id} title={option.value} value={option} />)}
    </BaseAsyncSelect>
  )
})
